import { createReducer, on } from '@ngrx/store';
import { SmartLinkImovel, SmartLinkImovelStatusEnum } from '@shared/models/smart-link/smart-link.model';
import { setImoveis, addImovel, removeImovel, moveImovel, setContadores, resetSmartLink } from '../actions/smart-link.action';

export interface SmartLinkStatusSlice {
  imoveis: SmartLinkImovel[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  loaded: boolean;
}

export interface SmartLinkState {
  [SmartLinkImovelStatusEnum.ANUNCIADO]: SmartLinkStatusSlice;
  [SmartLinkImovelStatusEnum.VENDIDO]: SmartLinkStatusSlice;
  [SmartLinkImovelStatusEnum.RASCUNHO]: SmartLinkStatusSlice;
  [SmartLinkImovelStatusEnum.ARQUIVADO]: SmartLinkStatusSlice;
}

const emptySlice: SmartLinkStatusSlice = { imoveis: [], totalItems: 0, currentPage: 1, totalPages: 1, loaded: false };

const initialState: SmartLinkState = {
  [SmartLinkImovelStatusEnum.ANUNCIADO]: { ...emptySlice },
  [SmartLinkImovelStatusEnum.VENDIDO]: { ...emptySlice },
  [SmartLinkImovelStatusEnum.RASCUNHO]: { ...emptySlice },
  [SmartLinkImovelStatusEnum.ARQUIVADO]: { ...emptySlice },
};

const ITEMS_PER_PAGE = 8;

const ABAS: SmartLinkImovelStatusEnum[] = [
  SmartLinkImovelStatusEnum.ANUNCIADO,
  SmartLinkImovelStatusEnum.VENDIDO,
  SmartLinkImovelStatusEnum.RASCUNHO,
  SmartLinkImovelStatusEnum.ARQUIVADO,
];

export const smartLinkReducer = createReducer(
  initialState,

  on(setImoveis, (state, { status, imoveis, totalItems, currentPage, totalPages, append }) => ({
    ...state,
    [status]: {
      // append=true (scroll infinito mobile) concatena; senão substitui (paginação desktop)
      imoveis: append ? [...state[status].imoveis, ...imoveis] : imoveis,
      totalItems,
      currentPage,
      totalPages,
      loaded: true,
    },
  })),

  on(addImovel, (state, { imovel }) => {
    const status = imovel.status as SmartLinkImovelStatusEnum;
    const slice = state[status];
    const newImoveis = [imovel, ...slice.imoveis];
    const newTotal = slice.totalItems + 1;
    return {
      ...state,
      [status]: {
        ...slice,
        imoveis: newImoveis,
        totalItems: newTotal,
        totalPages: Math.ceil(newTotal / ITEMS_PER_PAGE),
      },
    };
  }),

  on(removeImovel, (state, { id, status }) => {
    const slice = state[status];
    const newImoveis = slice.imoveis.filter(i => i._id !== id);
    const newTotal = slice.totalItems - 1;
    return {
      ...state,
      [status]: {
        ...slice,
        imoveis: newImoveis,
        totalItems: newTotal,
        totalPages: Math.ceil(newTotal / ITEMS_PER_PAGE) || 1,
      },
    };
  }),

  on(resetSmartLink, () => initialState),

  on(setContadores, (state, { contadores }) => {
    const next = { ...state };
    for (const status of ABAS) {
      const count = contadores[status];
      // Resposta sem a chave: mantém a slice como está (totalItems e
      // totalPages juntos, sem recalcular um e manter o outro).
      if (typeof count !== 'number') continue;
      next[status] = {
        ...state[status],
        totalItems: count,
        totalPages: Math.ceil(count / ITEMS_PER_PAGE) || 1,
      };
    }
    return next;
  }),

  on(moveImovel, (state, { imovel, fromStatus }) => {
    const toStatus = imovel.status as SmartLinkImovelStatusEnum;
    const fromSlice = state[fromStatus];
    const toSlice = state[toStatus];

    const fromImoveis = fromSlice.imoveis.filter(i => i._id !== imovel._id);
    const fromTotal = fromSlice.totalItems - 1;

    const toImoveis = [imovel, ...toSlice.imoveis];
    const toTotal = toSlice.totalItems + 1;

    return {
      ...state,
      [fromStatus]: {
        ...fromSlice,
        imoveis: fromImoveis,
        totalItems: fromTotal,
        totalPages: Math.ceil(fromTotal / ITEMS_PER_PAGE) || 1,
      },
      [toStatus]: {
        ...toSlice,
        imoveis: toImoveis,
        totalItems: toTotal,
        totalPages: Math.ceil(toTotal / ITEMS_PER_PAGE),
      },
    };
  }),
);
